import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import AccountContext from '../../../services/AccountContext';
import useScript from '../../../edcom-ui/hooks/useScript';
import track from '../../../services/track';
import styles from './SaveToDrive.module.less';
import { Modal, Button } from '../../../edcom-ui';

const CLIENT_ID =
  '283427801117-gcsiu9p714dc31684r15mdoks9vupn6a.apps.googleusercontent.com';
const SCOPE_DRIVEFILE = 'https://www.googleapis.com/auth/drive.file';
const SCOPE_DRIVECONFIG = 'https://www.googleapis.com/auth/drive.appdata';
const SCOPES = SCOPE_DRIVEFILE + ' ' + SCOPE_DRIVECONFIG;

const SAVE_STATE = {
  Save: 'Save',
  Saving: 'Saving ...',
  Saved: 'Saved',
};

const MISSING_ACCESS_DRIVEFILE = 'drive files';
const MISSING_ACCESS_DRIVECONFIG = 'drive configuration data';

const iso_8859_15_table = {
  338: 188,
  339: 189,
  352: 166,
  353: 168,
  376: 190,
  381: 180,
  382: 184,
  8364: 164,
};

function SaveToDrive({
  name,
  downloadHref,
  contentType,
  contentTypeDisplayName,
}) {
  const { isPremium } = useContext(AccountContext);

  // isPremium comes through as null if no one is logged in, so let's make this flag a true boolean.
  const premiumUser = isPremium === true;
  const [saveState, setSaveState] = useState(SAVE_STATE.Save);
  const [isOpen, setOpen] = useState(false);
  const [missingAccessText, setMissingAccessText] = useState('');

  // See https://stackoverflow.com/questions/44974003/recover-arraybuffer-from-xhr-responsetext/52110487#52110487
  const iso_8859_15_to_uint8array = iso_8859_15_str => {
    let buf = new ArrayBuffer(iso_8859_15_str.length);
    let bufView = new Uint8Array(buf);

    for (let i = 0, strLen = iso_8859_15_str.length; i < strLen; i++) {
      let octet = iso_8859_15_str.charCodeAt(i);
      // eslint-disable-next-line no-prototype-builtins
      if (iso_8859_15_table.hasOwnProperty(octet)) {
        octet = iso_8859_15_table[octet];
      }

      bufView[i] = octet;
    }

    return bufView;
  };

  const retrievePDF = async (downloadHref, name, token, setSaveState) => {
    const xmlHttp = new XMLHttpRequest();

    // eslint-disable-next-line func-names
    xmlHttp.onreadystatechange = function() {
      if (xmlHttp.status > 200) {
        // error
        setSaveState(SAVE_STATE.Save);
        throw new Error('Unable to retrieve worksheet.');
      } else if (xmlHttp.readyState === 4 && xmlHttp.status === 200) {
        uploadFile(
          downloadHref,
          name,
          token,
          new Blob([iso_8859_15_to_uint8array(xmlHttp.responseText)], {
            type: 'application/pdf',
          }),
          setSaveState
        );
      }
    };

    xmlHttp.open('GET', downloadHref, true);
    xmlHttp.setRequestHeader('Content-type', 'application/pdf');
    xmlHttp.overrideMimeType('text/plain; charset=ISO-8859-15');
    xmlHttp.type = 'arraybuffer';
    xmlHttp.send();
  };

  // eslint-disable-next-line no-unused-vars
  const uploadFile = async (downloadHref, name, token, blob, setSaveState) => {
    const xmlHttp = new XMLHttpRequest();

    // eslint-disable-next-line func-names
    xmlHttp.onreadystatechange = function() {
      if (xmlHttp.status > 200) {
        setSaveState(SAVE_STATE.Save);
        throw new Error('Unable to save worksheet to drive.');
      } else if (xmlHttp.readyState === 4 && xmlHttp.status === 200) {
        // ok
        setSaveState(SAVE_STATE.Saved);
      }
    };

    xmlHttp.open(
      'POST',
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
      true
    );

    // Set items into header
    xmlHttp.setRequestHeader('Authorization', 'Bearer ' + token);

    var metadata = {
      name: name + '.pdf',
      mimeType: 'application/pdf',
    };

    var form = new FormData();
    form.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );
    form.append('file', blob);

    xmlHttp.responseType = 'json';
    xmlHttp.send(form);
  };

  const trackSaveToDrive = () => {
    track(
      'Downloaded Content',
      {
        contentType: contentType,
        method: 'google-drive',
        section: `${contentTypeDisplayName} Detail`,
        name: name,
      },
      {
        eventCategory: contentType,
        eventAction: 'save to drive',
        eventLabel: `${contentTypeDisplayName} Download - Google Drive`,
        metric1: 1,
      }
    );
  };

  const handleClickSaveToDrive = setSaveState => {
    trackSaveToDrive();
    // eslint-disable-next-line no-undef
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: '',
    });

    tokenClient.callback = resp => {
      if (resp.error !== undefined) {
        // If user go through the google drive access granting process without granting any new access, we will
        // get resp.error = "access_denied" and an undefined resp.scope. Under this situation, since google.account.oauth2
        // API does not provide a way to grab previously granted scope(s) without a valid response, we are using generic
        // language to inform user that the worksheet cannot be saved due to insufficient access being granted.
        if (resp.error === 'access_denied') {
          setMissingAccessText(
            'It looks like we don’t have the necessary permissions to save this worksheet to your Google Drive.'
          );
        } else {
          setMissingAccessText(
            'Something unexpected happened and the worksheet could not be saved to your drive, please try again.'
          );
        }
        setOpen(true);
      } else {
        // Check if all access is granted.
        // If some access is still missing, open the popup modal to inform user.
        // Otherwise, save the file to drive and update "Save" button to "Saved" status.
        const scope = resp.scope;
        const driveFileAccess = scope.includes(SCOPE_DRIVEFILE);
        const driveAppDataAccess = scope.includes(SCOPE_DRIVECONFIG);
        let missingAccessItems;
        setSaveState(SAVE_STATE.Save);
        if (!driveFileAccess || !driveAppDataAccess) {
          if (driveFileAccess && !driveAppDataAccess) {
            missingAccessItems = MISSING_ACCESS_DRIVECONFIG;
          } else if (!driveFileAccess && driveAppDataAccess) {
            missingAccessItems = MISSING_ACCESS_DRIVEFILE;
          } else {
            missingAccessItems =
              MISSING_ACCESS_DRIVECONFIG + ' and ' + MISSING_ACCESS_DRIVEFILE;
          }

          // Insufficient access granted, set the alert message and turn on the open flag for popup modal.
          setMissingAccessText(
            'It looks like we don’t have the necessary permissions to save this worksheet to your Google Drive.' +
              '\nAccess to your ' +
              missingAccessItems +
              ' is missing.'
          );
          setOpen(true);
        } else {
          setSaveState(SAVE_STATE.Saving);

          // Now upload the file.
          // see: https://developers.google.com/drive/api/guides/manage-uploads#simple
          // see: https://phppot.com/javascript/google-drive-upload-javascript/
          retrievePDF(downloadHref, name, resp.access_token, setSaveState);
        }
      }
    };

    tokenClient.requestAccessToken({ prompt: '' });
  };

  useScript('https://accounts.google.com/gsi/client', !premiumUser);

  const buttonText = saveState;

  let saveButton;
  let title;

  switch (saveState) {
    case SAVE_STATE.Save:
      title = 'Save to your google drive';

      saveButton = (
        <div
          id="saveToDrive"
          className={clsx(styles.saveToDriveButton)}
          onClick={() => {
            handleClickSaveToDrive(setSaveState);
          }}
        >
          <div className={clsx(styles.saveToDriveImage)} />
          <div className={clsx(styles.saveToDriveText)}>{buttonText}</div>
        </div>
      );
      break;
    case SAVE_STATE.Saving:
      saveButton = (
        <div
          id="saveToDrive"
          className={clsx(styles.saveToDriveButton, styles.saving)}
        >
          <div className={clsx(styles.saveToDriveText)}>{buttonText}</div>
        </div>
      );
      break;
    case SAVE_STATE.Saved:
      title = 'Saved to your google drive';

      saveButton = (
        <div
          id="saveToDrive"
          className={clsx(styles.saveToDriveButton, styles.saved)}
          onClick={() => {
            handleClickSaveToDrive(setSaveState);
          }}
        >
          <div className={clsx(styles.saveToDriveImage)} />
          <div className={clsx(styles.saveToDriveText, styles.saved)}>
            {buttonText}
          </div>
        </div>
      );
      break;
    default:
  }

  return (
    <Fragment>
      <div
        className={clsx(styles.saveToDrive, { [styles.hide]: !premiumUser })}
        data-tooltip=""
        title={title}
      >
        {saveButton}
      </div>
      {/* The popup modal alerting user of insufficient access being granted */}
      {isOpen && (
        <Modal
          className={styles.insufficientAccessPopUp}
          open={isOpen}
          onClose={() => setOpen(false)}
          title="Insufficient Access"
          size="small"
        >
          <div className={styles.contentSection}>
            <h3>Unable to Save to Google Drive</h3>
            <p>{missingAccessText}</p>
            <div className={styles.closeButton}>
              <Button
                color="tertiary"
                onClick={() => setOpen(false)}
                className={styles.buttonContent}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Fragment>
  );
}

SaveToDrive.propTypes = {
  name: PropTypes.string.isRequired,
  contentType: PropTypes.string.isRequired,
  contentTypeDisplayName: PropTypes.string.isRequired,
  downloadHref: PropTypes.string.isRequired,
};

export default SaveToDrive;
